import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SearchableMultiSelect from '../components/SearchableMultiSelect';
import { CheckCircle, Lock, Calendar, MessageCircle, Stethoscope, Loader2, User } from 'lucide-react';
import { formOptions } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const benefits = [
  { icon: Stethoscope, title: "Doctor-Guided Assessment", text: "Speak with our team to understand which program fits your health needs." },
  { icon: Calendar, title: "Personalized Plan", text: "Receive a customized plan based on your medical history and lifestyle." },
  { icon: MessageCircle, title: "Ongoing Support", text: "Stay connected with us via WhatsApp and scheduled follow-ups." }
];

// Relationships with an implied gender (field hidden, value auto-set)
const AUTO_GENDER = { my_husband: 'male', my_wife: 'female', my_father: 'male', my_mother: 'female' };
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const inputClass = (hasError) =>
  `w-full bg-slate-800/70 border ${hasError ? 'border-red-500' : 'border-slate-700'} rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500`;

const emptyBenef = () => ({ gender: '', age: '', height: '', weight: '', concern: '', relationshipCustom: '' });

const BookConsultationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = (searchParams.get('from') || 'direct').slice(0, 60);

  const [formData, setFormData] = useState({
    fullName: '', email: '', mobile: '', whatsapp: '', country: '', message: ''
  });
  const [selected, setSelected] = useState([]); // beneficiary values, in click order
  const [beneficiaries, setBeneficiaries] = useState({}); // keyed by value
  const [sameAsMobile, setSameAsMobile] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [benefErrors, setBenefErrors] = useState({}); // keyed by value -> { field: msg }
  const [consent, setConsent] = useState(false);

  // Mutually-exclusive spouse rules + selected-state for the searchable selector
  const forOptions = useMemo(() => {
    const husbandSel = selected.includes('my_husband');
    const wifeSel = selected.includes('my_wife');
    return formOptions.consultationFor.map((o) => {
      let disabled = false;
      if (o.value === 'my_wife' && husbandSel) disabled = true;
      if (o.value === 'my_husband' && wifeSel) disabled = true;
      return { ...o, disabled };
    });
  }, [selected]);

  // Infer a beneficiary's gender. "Myself" is inferred from the spouse selected
  // (My Wife => user is Male, My Husband => user is Female). Other relationships
  // use their own implied gender. Returns null when the user must choose.
  const inferredGenderFor = (value) => {
    if (value === 'myself') {
      if (selected.includes('my_wife')) return 'male';
      if (selected.includes('my_husband')) return 'female';
      return null;
    }
    return AUTO_GENDER[value] || null;
  };

  // Female-exclusive concerns (hidden for male / other / prefer-not-to-say).
  const FEMALE_ONLY_CONCERNS = ['pregnancy', 'postpartum', 'pcos', 'menstrual', 'menopause'];
  // My Mother / My Father hide these regardless of gender.
  const REL_HIDDEN_CONCERNS = ['pregnancy', 'postpartum', 'fertility'];

  // Smart concern list for a beneficiary based on relationship + (selected/inferred) gender.
  const concernsFor = (value, gender) => {
    if (value === 'my_mother' || value === 'my_father') {
      return formOptions.concerns.filter((c) => !REL_HIDDEN_CONCERNS.includes(c.value));
    }
    if (gender === 'female') return formOptions.concerns; // all concerns
    if (gender === 'male' || gender === 'other' || gender === 'prefer_not_to_say') {
      // hide female-exclusive conditions, always keep Fertility
      return formOptions.concerns.filter((c) => !FEMALE_ONLY_CONCERNS.includes(c.value));
    }
    return formOptions.concerns; // gender not chosen yet
  };

  // Data integrity: clear a beneficiary's concern if it becomes invalid after
  // gender/relationship changes, so only valid values reach the database.
  useEffect(() => {
    setBeneficiaries((prev) => {
      let changed = false;
      const next = { ...prev };
      selected.forEach((v) => {
        const b = prev[v];
        if (!b || !b.concern) return;
        const g = inferredGenderFor(v) || b.gender || '';
        const stillValid = concernsFor(v, g).some((c) => c.value === b.concern);
        if (!stillValid) { next[v] = { ...b, concern: '' }; changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, beneficiaries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const addBenef = (value) => {
    setSelected((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setBeneficiaries((b) => ({ ...b, [value]: { ...emptyBenef(), gender: inferredGenderFor(value) || '' } }));
    if (errors.consultationFor) setErrors((p) => ({ ...p, consultationFor: '' }));
  };

  const removeBenef = (value) => {
    setSelected((prev) => prev.filter((v) => v !== value));
    setBeneficiaries((b) => { const c = { ...b }; delete c[value]; return c; });
    setBenefErrors((prev) => { const c = { ...prev }; delete c[value]; return c; });
  };

  const toggleFor = (value) => {
    if (selected.includes(value)) removeBenef(value);
    else addBenef(value);
  };

  const updateBenef = (value, field, val) => {
    setBeneficiaries((prev) => ({ ...prev, [value]: { ...prev[value], [field]: val } }));
    setBenefErrors((prev) => {
      if (!prev[value] || !prev[value][field]) return prev;
      const copy = { ...prev, [value]: { ...prev[value] } };
      delete copy[value][field];
      return copy;
    });
  };

  const optLabel = (value) => (formOptions.consultationFor.find((o) => o.value === value) || {}).label || value;
  const concernLabel = (v) => (formOptions.concerns.find((c) => c.value === v) || {}).label || v;

  const validate = () => {
    const err = {};
    if (!formData.fullName.trim()) err.fullName = 'Required';
    if (!formData.email.trim()) err.email = 'Required';
    if (!formData.mobile.trim()) err.mobile = 'Required';
    if (!sameAsMobile && !formData.whatsapp.trim()) err.whatsapp = 'Required';
    if (!formData.country.trim()) err.country = 'Required';
    if (selected.length === 0) err.consultationFor = 'Please select at least one option';
    if (!consent) err.consent = 'Please accept the medical disclaimer to continue.';

    const bErr = {};
    selected.forEach((value) => {
      const b = beneficiaries[value] || emptyBenef();
      const e = {};
      const needsGender = !inferredGenderFor(value);
      if (value === 'other' && !b.relationshipCustom.trim()) e.relationshipCustom = 'Required';
      if (needsGender && !b.gender) e.gender = 'Required';
      const age = Number(b.age);
      if (!b.age) e.age = 'Required';
      else if (isNaN(age) || age < 1 || age > 120) e.age = '1–120';
      const height = Number(b.height);
      if (!b.height) e.height = 'Required';
      else if (isNaN(height) || height < 80 || height > 250) e.height = '80–250';
      const weight = Number(b.weight);
      if (!b.weight) e.weight = 'Required';
      else if (isNaN(weight) || weight < 20 || weight > 300) e.weight = '20–300';
      if (!b.concern) e.concern = 'Required';
      if (Object.keys(e).length) bErr[value] = e;
    });

    setErrors(err);
    setBenefErrors(bErr);
    return Object.keys(err).length === 0 && Object.keys(bErr).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    const whatsappNumber = sameAsMobile ? formData.mobile : formData.whatsapp;
    const beneficiaryList = selected.map((value) => {
      const b = beneficiaries[value];
      const gender = inferredGenderFor(value) || b.gender;
      const relationship = value === 'other' ? b.relationshipCustom.trim() : optLabel(value);
      return {
        relationship,
        relationship_type: value,
        gender,
        age: Number(b.age),
        height_cm: Number(b.height),
        weight_kg: Number(b.weight),
        primary_concern: b.concern,
      };
    });
    const first = beneficiaryList[0];
    const forLabels = selected.map((v) => (v === 'other' ? (beneficiaries[v].relationshipCustom.trim() || 'Other') : optLabel(v)));

    try {
      await axios.post(`${API}/consultations`, {
        full_name: formData.fullName,
        age: first.age,
        gender: first.gender,
        height_cm: first.height_cm,
        weight_kg: first.weight_kg,
        country: formData.country,
        mobile: formData.mobile,
        whatsapp: whatsappNumber,
        email: formData.email,
        consultation_for: forLabels,
        beneficiaries: beneficiaryList,
        health_conditions: [],
        primary_goal: first.primary_concern,
        additional_info: formData.message,
        source: source,
        consent: consent,
      });
      navigate('/thank-you');
    } catch (err) {
      console.error(err);
      setErrors({ submit: 'Something went wrong. Please try again or message us on WhatsApp.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Book Your Free Consultation | TCUBE FITNESS™</title>
        <meta name="description" content="Book a free doctor-guided consultation with TCUBE FITNESS to discover which fertility, pregnancy, postpartum or fitness program suits your needs." />
        <link rel="canonical" href="https://tcubefitness.com/book-consultation" />
      </Helmet>

      <AnnouncementBanner />
      <Header />

      <main id="main-content" className="pt-32 sm:pt-36 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Book Your <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Free Consultation</span>
            </h1>
            <p className="text-slate-400 text-base sm:text-lg">
              Speak with our team and understand which program best suits your health goals.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Left: Benefits */}
            <div className="space-y-5">
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-5">What You'll Get</h2>
                <ul className="space-y-5">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-500/15 rounded-xl flex items-center justify-center text-blue-400 flex-shrink-0">
                        <b.icon className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1 text-sm sm:text-base">{b.title}</h3>
                        <p className="text-slate-400 text-sm">{b.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5 sm:p-6 flex gap-3 items-start">
                <Lock className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h3 className="text-emerald-300 font-semibold text-sm sm:text-base mb-1">Your privacy matters</h3>
                  <p className="text-slate-400 text-sm">All health information you share is kept strictly confidential and used only to design your personalized program.</p>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <form
              onSubmit={handleSubmit}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-5"
              data-testid="book-consultation-form"
              noValidate
            >
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-1.5">Full Name *</label>
                <input id="fullName" name="fullName" value={formData.fullName} onChange={handleChange}
                  className={inputClass(errors.fullName)} data-testid="book-fullname-input" />
                {errors.fullName && <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1.5">Email Address *</label>
                <input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                  className={inputClass(errors.email)} data-testid="book-email-input" />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="mobile" className="block text-sm font-medium text-slate-300 mb-1.5">Mobile Number * <span className="text-slate-500 font-normal">(Primary contact)</span></label>
                <input id="mobile" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="+91 XXXXXXXXXX"
                  className={inputClass(errors.mobile)} data-testid="book-mobile-input" />
                {errors.mobile && <p className="text-red-400 text-xs mt-1">{errors.mobile}</p>}
              </div>

              <div>
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input type="checkbox" checked={sameAsMobile}
                    onChange={(e) => { setSameAsMobile(e.target.checked); if (errors.whatsapp) setErrors((p) => ({ ...p, whatsapp: '' })); }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500"
                    data-testid="book-same-as-mobile-checkbox" />
                  <span className="text-sm text-slate-300">WhatsApp number same as Mobile Number</span>
                </label>
                {!sameAsMobile && (
                  <div className="mt-3">
                    <label htmlFor="whatsapp" className="block text-sm font-medium text-slate-300 mb-1.5">WhatsApp Number</label>
                    <input id="whatsapp" name="whatsapp" value={formData.whatsapp} onChange={handleChange} placeholder="+91 XXXXXXXXXX"
                      className={inputClass(errors.whatsapp)} data-testid="book-whatsapp-input" />
                    {errors.whatsapp && <p className="text-red-400 text-xs mt-1">{errors.whatsapp}</p>}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-slate-300 mb-1.5">City / Country *</label>
                <input id="country" name="country" value={formData.country} onChange={handleChange}
                  className={inputClass(errors.country)} data-testid="book-country-input" />
                {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
              </div>

              {/* Who is this consultation for? Searchable multi-select */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Who is this consultation for? *</label>
                <SearchableMultiSelect
                  options={forOptions}
                  value={selected}
                  onToggle={toggleFor}
                  onRemove={removeBenef}
                  placeholder="Search & select one or more…"
                  testId="book-consultation-for"
                />
                {errors.consultationFor && <p className="text-red-400 text-xs mt-1">{errors.consultationFor}</p>}
              </div>

              {/* Dynamic beneficiary cards */}
              <div className="space-y-4" data-testid="beneficiary-cards">
                <AnimatePresence initial={false}>
                  {selected.map((value) => {
                    const b = beneficiaries[value] || emptyBenef();
                    const be = benefErrors[value] || {};
                    const inferredGender = inferredGenderFor(value);
                    const needsGender = !inferredGender;
                    const isOther = value === 'other';
                    const title = isOther ? (b.relationshipCustom.trim() || 'Other') : optLabel(value);
                    return (
                      <motion.div
                        key={value}
                        layout
                        initial={{ opacity: 0, height: 0, scale: 0.98 }}
                        animate={{ opacity: 1, height: 'auto', scale: 1 }}
                        exit={{ opacity: 0, height: 0, scale: 0.98 }}
                        transition={{ duration: 0.22, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 sm:p-5 space-y-4" data-testid={`beneficiary-card-${value}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-400 flex-shrink-0">
                              <User className="w-4 h-4" aria-hidden="true" />
                            </div>
                            <h3 className="text-white font-semibold text-sm sm:text-base">{title}</h3>
                            {!needsGender && (
                              <span className="ml-auto text-xs text-slate-400 capitalize" data-testid={`beneficiary-autogender-${value}`}>
                                {inferredGender}
                              </span>
                            )}
                          </div>

                          {isOther && (
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1.5">Relationship *</label>
                              <input value={b.relationshipCustom} onChange={(ev) => updateBenef(value, 'relationshipCustom', ev.target.value)}
                                placeholder="e.g. Grandmother, Colleague"
                                className={inputClass(be.relationshipCustom)} data-testid={`beneficiary-${value}-relationship`} />
                              {be.relationshipCustom && <p className="text-red-400 text-xs mt-1">{be.relationshipCustom}</p>}
                            </div>
                          )}

                          {needsGender && (
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1.5">Gender *</label>
                              <select value={b.gender} onChange={(ev) => updateBenef(value, 'gender', ev.target.value)}
                                className={inputClass(be.gender)} data-testid={`beneficiary-${value}-gender`}>
                                <option value="">Select gender</option>
                                {genderOptions.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                              </select>
                              {be.gender && <p className="text-red-400 text-xs mt-1">{be.gender}</p>}
                            </div>
                          )}

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1.5">Age *</label>
                              <input type="number" min="1" max="120" value={b.age} onChange={(ev) => updateBenef(value, 'age', ev.target.value)}
                                className={inputClass(be.age)} data-testid={`beneficiary-${value}-age`} />
                              {be.age && <p className="text-red-400 text-xs mt-1">{be.age}</p>}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1.5">Height (cm) *</label>
                              <input type="number" min="80" max="250" value={b.height} onChange={(ev) => updateBenef(value, 'height', ev.target.value)}
                                className={inputClass(be.height)} data-testid={`beneficiary-${value}-height`} />
                              {be.height && <p className="text-red-400 text-xs mt-1">{be.height}</p>}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1.5">Weight (kg) *</label>
                              <input type="number" min="20" max="300" step="0.1" value={b.weight} onChange={(ev) => updateBenef(value, 'weight', ev.target.value)}
                                className={inputClass(be.weight)} data-testid={`beneficiary-${value}-weight`} />
                              {be.weight && <p className="text-red-400 text-xs mt-1">{be.weight}</p>}
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-300 mb-1.5">Primary Concern *</label>
                            <select value={b.concern} onChange={(ev) => updateBenef(value, 'concern', ev.target.value)}
                              className={inputClass(be.concern)} data-testid={`beneficiary-${value}-concern`}>
                              <option value="">Select a concern</option>
                              {concernsFor(value, inferredGender || b.gender).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                            {be.concern && <p className="text-red-400 text-xs mt-1">{be.concern}</p>}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-1.5">Message (optional)</label>
                <textarea id="message" name="message" rows="3" value={formData.message} onChange={handleChange}
                  className="w-full bg-slate-800/70 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
                  data-testid="book-message-input" />
              </div>

              <div className="flex items-start gap-2.5">
                <input id="consent" type="checkbox" checked={consent}
                  onChange={(e) => { setConsent(e.target.checked); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                  className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  data-testid="book-consent-checkbox" />
                <label htmlFor="consent" className="text-slate-400 text-xs leading-relaxed">
                  I understand this is doctor-guided lifestyle &amp; nutrition coaching, not a substitute for medical care, and I agree to be contacted. I have read the{' '}
                  <Link to="/medical-disclaimer" target="_blank" className="text-blue-400 hover:underline">Medical Disclaimer</Link>.
                </label>
              </div>
              {errors.consent && <p className="text-red-400 text-xs" data-testid="book-consent-error">{errors.consent}</p>}

              {errors.submit && <p className="text-red-400 text-sm">{errors.submit}</p>}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-60 text-white px-6 py-3.5 rounded-full font-semibold transition-all shadow-lg shadow-blue-500/25"
                data-testid="book-submit-btn"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                {isSubmitting ? 'Submitting…' : 'Confirm My Free Consultation'}
              </button>

              <p className="text-slate-500 text-xs text-center">By submitting, you agree to be contacted regarding your consultation.</p>
            </form>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default BookConsultationPage;
